import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Tablet, Smartphone, ShieldCheck, WifiOff, Zap, Users, Check, ArrowRight, Clock,
  KeyRound, MapPin, CheckCircle2, ChevronDown, Sparkles, Building2, Wrench, HeartPulse,
  ShoppingBag, HardHat, DollarSign, Laptop, RefreshCw
} from 'lucide-react';

const steps = [
  {
    step: '01',
    title: 'Open Kiosk on any tablet or iPad',
    desc: 'Log in on any Apple iPad, budget Android tablet, smartphone, or laptop browser. Tap "Switch to Kiosk Mode" to lock the terminal into attendance mode.'
  },
  {
    step: '02',
    title: 'Place at your site entrance or counter',
    desc: 'Set the tablet on your front counter, mount it to the wall, or keep it in the manager\'s office. No special brackets, electricians, or wiring required.'
  },
  {
    step: '03',
    title: 'Staff punch in with 4-digit PINs',
    desc: 'Each worker enters their unique 4-digit PIN. The screen confirms attendance in under 2 seconds and immediately resets for the next person.'
  },
  {
    step: '04',
    title: 'Hours calculate live for payroll',
    desc: 'Start times, breaks, overtime, and statutory UK 12.07% holiday accrual update in real time. Export ready-to-run timesheets to Xero or Excel.'
  }
];

const features = [
  {
    icon: Tablet,
    title: 'No Hardware Leases or Contracts',
    description: 'Save £500–£1,200 per site. KithHR runs on standard iPads, Amazon Fire tablets, Android terminals, or desktop PCs you already have.'
  },
  {
    icon: KeyRound,
    title: 'Private 4-Digit PIN Codes',
    description: 'Every team member gets a private PIN. Eliminates coworkers clocking in for late friends with transparent timestamp audit trails.'
  },
  {
    icon: WifiOff,
    title: 'Keeps Working if Internet Drops',
    description: 'If site Wi-Fi drops out, the kiosk continues accepting PIN punches offline and synchronises timestamps the second connection returns.'
  },
  {
    icon: ShieldCheck,
    title: 'UK GDPR & Biometric-Free',
    description: 'No fingerprint scanning or invasive facial recognition databases. 100% compliant with UK employment law and worker privacy regulations.'
  },
  {
    icon: Clock,
    title: 'Automated Break Deductions',
    description: 'Enforce statutory 20-minute rest breaks or unpaid lunch deductions automatically without manual calculator work at payroll time.'
  },
  {
    icon: Zap,
    title: 'Instant Xero & CSV Export',
    description: 'Accurate gross pay hours flow directly into your payroll software. Cut out manual timesheet re-keying each week.'
  }
];

const industries = [
  {
    icon: Building2,
    title: 'Hospitality & Restaurants',
    desc: 'Front-of-house staff and kitchen crew clock in seamlessly on a single counter iPad without downloading personal apps.'
  },
  {
    icon: HardHat,
    title: 'Construction & Site Cabins',
    desc: 'Tough container environments with spotty cellular coverage. Operates completely offline with local timestamp caching.'
  },
  {
    icon: HeartPulse,
    title: 'Care Homes & Healthcare',
    desc: 'Hygienic, touch-friendly PIN clocking with instant shift overlap records and accurate statutory pay tracking.'
  },
  {
    icon: ShoppingBag,
    title: 'Retail Stores & Boutiques',
    desc: 'Shared till or back-room tablet kiosk. Staff punch in before opening with automatic late arrival flags.'
  }
];

const faqs = [
  {
    q: 'Can I use an old iPad or cheap Android tablet as a kiosk?',
    a: 'Yes. KithHR is web-based and highly optimised. Any Apple iPad (iOS 13+), budget Amazon Fire tablet, Android device, or Chromebook works immediately in full-screen kiosk mode.'
  },
  {
    q: 'How does KithHR prevent employees from exiting the kiosk screen?',
    a: 'Managers can enable iOS "Guided Access" or Android "App Pinning" with one tap in device settings. This locks the tablet strictly to the KithHR attendance keypad so staff cannot access other apps or web browsing.'
  },
  {
    q: 'What happens if the site Wi-Fi goes down during shift changes?',
    a: 'KithHR includes built-in offline caching. Employees continue entering their PINs as normal. The kiosk stores encrypted timestamps locally on the tablet and automatically pushes them to the cloud the moment internet connectivity restores.'
  },
  {
    q: 'Can managers monitor attendance remotely while away from the site?',
    a: 'Yes. The moment an employee clocks in at the kiosk, their status updates in real-time on the manager\'s phone or desktop dashboard, complete with late flags and hours tallying.'
  },
  {
    q: 'Is there a limit on how many staff can use a single kiosk tablet?',
    a: 'No limit. A single tablet kiosk can easily handle 50+ staff punch-ins per shift. KithHR is free forever for teams up to 15 employees, and only £3.49/user/month for larger teams.'
  }
];

const TabletTimeClockKiosk: React.FC = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState<string>('');
  const [kioskStatus, setKioskStatus] = useState<string | null>(null);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const handleKeyClick = (val: string) => {
    if (kioskStatus) setKioskStatus(null);
    if (val === 'C') {
      setPin('');
      return;
    }
    if (pin.length < 4) {
      const newPin = pin + val;
      setPin(newPin);
      if (newPin.length === 4) {
        setTimeout(() => {
          setKioskStatus('Verified: Alex Morgan (Chef) clocked in at 08:00 AM ✅');
          setPin('');
        }, 300);
      }
    }
  };

  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: 'KithHR Tablet Time Clock Kiosk',
        operatingSystem: 'iOS, Android, Windows, Web, PWA',
        applicationCategory: 'BusinessApplication',
        url: 'https://kithhr.co.uk/tablet-time-clock-kiosk',
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: '0.00',
          highPrice: '3.49',
          priceCurrency: 'GBP'
        },
        description: 'Turn any iPad, tablet or phone into a fixed entrance time clock kiosk. 4-digit PIN clock-in, offline queue, zero hardware leases, and free for up to 15 staff.'
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0F172A] font-sans selection:bg-[#0062D2] selection:text-white">
      <SEO
        title="Tablet Time Clock Kiosk UK: Turn Any iPad or Android into a Staff Clock-In Terminal | KithHR"
        description="Turn any iPad, tablet, or phone into a fixed entrance time clock kiosk. 4-digit PIN clock-in, offline queue, zero hardware leases, and free for up to 15 staff."
        canonical="https://kithhr.co.uk/tablet-time-clock-kiosk"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJsonLd) }}
      />

      {/* HEADER / NAVIGATION */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E8E2D8] transition-all">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 font-extrabold text-2xl tracking-tight text-[#0F172A] hover:opacity-90">
            <img src="/logo.png" alt="KithHR Logo" className="w-9 h-9 object-contain" />
            <span>Kith<span className="text-[#0062D2]">HR</span></span>
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/nfc-card-clock-in"
              className="text-sm font-semibold text-[#475569] hover:text-[#0062D2] transition-colors hidden sm:block"
            >
              NFC Clock-In
            </Link>
            <Link
              to="/pricing"
              className="text-sm font-semibold text-[#475569] hover:text-[#0062D2] transition-colors hidden sm:block"
            >
              Pricing
            </Link>
            <button
              onClick={() => navigate('/login')}
              className="bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold text-sm px-5 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md active:scale-95 cursor-pointer"
            >
              Start Free (1-15 Staff)
            </button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-20 md:pt-24 md:pb-28 overflow-hidden border-b border-[#E8E2D8]">
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Heading & Context */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-[#005BB5] text-xs font-black tracking-wider uppercase">
                <Tablet size={14} /> Tablet Attendance Terminal · Zero Hardware Leases
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0F172A] leading-[1.12]">
                Turn any iPad or Android into a <span className="text-[#0062D2]">fixed entrance clock-in</span>.
              </h1>

              <p className="text-lg text-[#475569] max-w-xl mx-auto lg:mx-0 leading-relaxed font-normal">
                Set up a shared PIN punch clock on your front counter, kitchen wall, or warehouse cabin in 60 seconds. Staff enter their 4-digit PIN, hours calculate automatically, and timesheets sync straight to payroll.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-8 py-4 rounded-2xl text-base transition-all shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                >
                  Set Up Tablet Kiosk Free <ArrowRight size={18} />
                </button>
                <a
                  href="#simulator"
                  className="w-full sm:w-auto bg-white border border-[#E8E2D8] hover:bg-[#F5F1EB] text-[#0F172A] font-semibold px-6 py-4 rounded-2xl text-base transition-all text-center shadow-xs"
                >
                  Try Live PIN Demo ↓
                </a>
              </div>

              <div className="pt-4 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-xs text-[#64748B] font-medium">
                <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600 stroke-[3]" /> Free forever for up to 15 staff</span>
                <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600 stroke-[3]" /> Runs on any iPad or Android tablet</span>
                <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600 stroke-[3]" /> Works offline if Wi-Fi drops</span>
              </div>
            </div>

            {/* Right Column: Interactive Tablet Kiosk PIN Simulator */}
            <div id="simulator" className="lg:col-span-5 flex justify-center">
              <div className="w-full max-w-sm bg-white border-2 border-[#E8E2D8] rounded-[36px] p-6 shadow-2xl shadow-slate-200/60 relative">
                
                {/* Tablet Frame Header */}
                <div className="text-center pb-4 border-b border-[#E8E2D8]">
                  <div className="w-12 h-1 bg-[#E8E2D8] rounded-full mx-auto mb-3" />
                  <span className="text-[10px] uppercase font-black tracking-widest text-[#005BB5] bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                    KithHR Kiosk Active
                  </span>
                  <div className="text-xl font-bold text-[#0F172A] mt-2.5">
                    Enter Your 4-Digit PIN
                  </div>
                  <p className="text-xs text-[#64748B] mt-0.5">Alex Morgan (Chef) demo PIN: <strong>1 2 3 4</strong></p>
                </div>

                {/* PIN Display Dots */}
                <div className="py-6 flex justify-center items-center gap-4">
                  {[0, 1, 2, 3].map(i => (
                    <div
                      key={i}
                      className={`w-4 h-4 rounded-full transition-all duration-200 ${
                        pin.length > i
                          ? 'bg-[#0062D2] scale-110 shadow-xs'
                          : 'bg-[#F0ECE1] border border-[#E8E2D8]'
                      }`}
                    />
                  ))}
                </div>

                {/* Status Message */}
                {kioskStatus && (
                  <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-bold text-center animate-fade-in shadow-xs">
                    {kioskStatus}
                  </div>
                )}

                {/* Keypad */}
                <div className="grid grid-cols-3 gap-2.5 max-w-[260px] mx-auto">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '✓'].map(k => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => handleKeyClick(k)}
                      className={`h-13 rounded-2xl font-bold text-lg transition-all flex items-center justify-center cursor-pointer ${
                        k === 'C'
                          ? 'bg-[#FAF8F5] text-rose-600 hover:bg-rose-50 border border-[#E8E2D8]'
                          : k === '✓'
                          ? 'bg-[#0062D2] text-white hover:bg-[#004CB5] shadow-xs'
                          : 'bg-[#FAF8F5] hover:bg-[#F0ECE1] text-[#0F172A] border border-[#E8E2D8] shadow-2xs active:scale-95'
                      }`}
                    >
                      {k}
                    </button>
                  ))}
                </div>

                <p className="text-[11px] text-[#64748B] text-center mt-5">
                  💡 Tap the keypad to test how fast the 2-second check-in works.
                </p>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* COST COMPARISON */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FAF8F5]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">
              Traditional punch clocks vs KithHR tablet kiosk
            </h2>
            <p className="text-[#475569] mt-2 text-base">
              Why leasing proprietary wall terminals is an unnecessary expense for UK small businesses.
            </p>
          </div>

          <div className="overflow-x-auto rounded-3xl border border-[#E8E2D8] bg-white shadow-xs">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#FAF8F5] border-b border-[#E8E2D8] text-[#475569] text-xs font-bold uppercase tracking-wider">
                <tr>
                  <th className="py-4 px-6">Cost / Requirement</th>
                  <th className="py-4 px-6 text-[#0062D2] font-black">KithHR Tablet Kiosk</th>
                  <th className="py-4 px-6">Traditional Punch Clocks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E2D8]">
                <tr className="hover:bg-[#FAF8F5]/60 transition-colors">
                  <td className="py-4 px-6 font-bold text-[#0F172A]">Hardware Purchase Cost</td>
                  <td className="py-4 px-6 text-[#005BB5] font-bold bg-blue-50/40">£0 (Use existing iPad or Android)</td>
                  <td className="py-4 px-6 text-[#64748B]">£600 – £1,500 per device</td>
                </tr>
                <tr className="hover:bg-[#FAF8F5]/60 transition-colors">
                  <td className="py-4 px-6 font-bold text-[#0F172A]">Installation &amp; Wiring</td>
                  <td className="py-4 px-6 text-[#005BB5] font-bold bg-blue-50/40">£0 (No electrician or wall drilling)</td>
                  <td className="py-4 px-6 text-[#64748B]">£200 – £400 electrician call-out</td>
                </tr>
                <tr className="hover:bg-[#FAF8F5]/60 transition-colors">
                  <td className="py-4 px-6 font-bold text-[#0F172A]">Monthly Software Cost</td>
                  <td className="py-4 px-6 text-[#005BB5] font-bold bg-blue-50/40">£0 for 1-15 staff (£3.49 for larger teams)</td>
                  <td className="py-4 px-6 text-[#64748B]">£40 – £120/month per terminal lease</td>
                </tr>
                <tr className="hover:bg-[#FAF8F5]/60 transition-colors">
                  <td className="py-4 px-6 font-bold text-[#0F172A]">Contract Lock-In</td>
                  <td className="py-4 px-6 text-[#005BB5] font-bold bg-blue-50/40">Zero contract (Cancel anytime)</td>
                  <td className="py-4 px-6 text-[#64748B]">Strict 24 to 36-month lease agreements</td>
                </tr>
                <tr className="hover:bg-[#FAF8F5]/60 transition-colors">
                  <td className="py-4 px-6 font-bold text-[#0F172A]">Payroll Integration</td>
                  <td className="py-4 px-6 text-[#005BB5] font-bold bg-blue-50/40">Instant Xero &amp; CSV export</td>
                  <td className="py-4 px-6 text-[#64748B]">Proprietary CSV or manual re-typing</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 4 STEPS TO DEPLOY */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">
              How to set up your tablet kiosk in 4 steps
            </h2>
            <p className="text-[#475569] mt-2 text-base">
              No technical expertise needed. Get your attendance terminal live in under two minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map(s => (
              <div key={s.step} className="p-6 rounded-2xl bg-white border border-[#E8E2D8] shadow-xs hover:border-[#0062D2]/40 transition-all">
                <div className="text-3xl font-black text-[#0062D2] font-mono mb-3">{s.step}</div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{s.title}</h3>
                <p className="text-xs text-[#64748B] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 FEATURES */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">
              Built for practical workplace attendance
            </h2>
            <p className="text-[#475569] mt-2 text-base">
              Reliable features designed specifically for shift managers and frontline workers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white border border-[#E8E2D8] shadow-xs hover:border-[#0062D2]/30 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#005BB5] flex items-center justify-center mb-4 border border-blue-200">
                  <f.icon size={20} />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{f.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTOR USE CASES */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">
              Trusted across UK shift businesses
            </h2>
            <p className="text-[#475569] mt-2 text-base">
              From busy restaurant kitchens to remote construction sites.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((ind, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white border border-[#E8E2D8] shadow-xs hover:border-[#0062D2]/30 transition-all">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-800 flex items-center justify-center mb-4 border border-emerald-200">
                  <ind.icon size={20} />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{ind.title}</h3>
                <p className="text-xs text-[#64748B] leading-relaxed">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQS SECTION */}
      <section className="py-20 md:py-28 border-b border-[#E8E2D8] bg-[#FAF8F5]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-[#0F172A]">Tablet kiosk questions answered</h2>
            <p className="text-[#475569] mt-2 text-sm">Clear details on tablet setup, security, and staff usage.</p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-[#E8E2D8] bg-white overflow-hidden transition-all shadow-xs"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between font-bold text-[#0F172A] hover:text-[#0062D2] transition-colors cursor-pointer"
                >
                  <span>{faq.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-[#64748B] transition-transform duration-300 ${activeFaq === idx ? 'rotate-180 text-[#0062D2]' : ''}`}
                  />
                </button>
                {activeFaq === idx && (
                  <div className="px-6 pb-5 text-sm text-[#475569] border-t border-[#E8E2D8] pt-3 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-20 md:py-28 bg-[#FDFBF7] text-center">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          <h2 className="text-3xl sm:text-5xl font-black text-[#0F172A] tracking-tight">
            Ready to set up your tablet kiosk?
          </h2>
          <p className="text-lg text-[#475569] max-w-xl mx-auto">
            Turn any tablet into a fixed entrance clock-in in two minutes. 100% free forever for up to 15 employees.
          </p>
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-[#0062D2] hover:bg-[#004CB5] text-white font-bold px-10 py-4 rounded-2xl text-lg transition-all shadow-lg shadow-blue-500/20 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
            >
              Get Started Free <ArrowRight size={20} />
            </button>
            <Link
              to="/nfc-card-clock-in"
              className="w-full sm:w-auto bg-white border border-[#E8E2D8] hover:bg-[#F5F1EB] text-[#0F172A] font-semibold px-8 py-4 rounded-2xl text-lg transition-all text-center shadow-xs"
            >
              Explore NFC Attendance
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E8E2D8] bg-white py-10 text-xs text-[#64748B]">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="KithHR" className="w-5 h-5 rounded-md" />
            <span className="font-bold text-[#0F172A]">KithHR Workforce Ltd</span>
          </div>
          <div className="flex items-center gap-6 text-[#64748B]">
            <Link to="/tablet-time-clock-kiosk" className="hover:text-[#0062D2]">Tablet Kiosk</Link>
            <Link to="/pin-time-clock" className="hover:text-[#0062D2]">PIN Clock</Link>
            <Link to="/pricing" className="hover:text-[#0062D2]">Pricing</Link>
            <Link to="/terms" className="hover:text-[#0062D2]">Terms</Link>
            <Link to="/privacy-policy" className="hover:text-[#0062D2]">Privacy</Link>
          </div>
          <p>© {new Date().getFullYear()} KithHR. Built for UK Businesses.</p>
        </div>
      </footer>
    </div>
  );
};

export default TabletTimeClockKiosk;
